import styled from "styled-components";
import { useEffect, useState } from "react";
import { X, Ban, AlertTriangle } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";
import { apiFetch } from "./api";

// Cancelling a walk-in.
//
// Its own dialog rather than the shared confirm, because the reason is typed HERE: askConfirm captures
// its callback when the dialog opens, so a callback closed over an empty string is what it would send.
//
// The blockers are fetched rather than inferred. Each one exists because the app cannot give that
// money back on its own — a captured deposit on a stay that never checks out has no refund path at
// all — so the honest answer is to name the blocker and point at /admin.
const Backdrop = styled.div`
  align-items: center;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1500;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid #b42318;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  max-width: 480px;
  width: 100%;
`;

const Head = styled.div`
  align-items: flex-start;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  gap: 0.6rem;
  padding: 0.85rem 1.1rem;
`;

const Title = styled.h3`
  align-items: center;
  color: #b42318;
  display: flex;
  flex: 1 1 auto;
  font-size: 1rem;
  font-weight: 800;
  gap: 0.35rem;
  margin: 0;
`;

const Close = styled.button`
  background: transparent;
  border: 0;
  color: ${theme.ink700};
  cursor: pointer;
  padding: 0.1rem;
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 0.8rem 1.1rem;
`;

const Intro = styled.p`
  color: #4a4f63;
  font-size: 0.84rem;
  line-height: 1.45;
  margin: 0 0 0.5rem;
`;

const Hard = styled.div`
  background: #fdecea;
  border-radius: 8px;
  color: #b42318;
  font-size: 0.82rem;
  font-weight: 800;
  margin-bottom: 0.7rem;
  padding: 0.45rem 0.6rem;
`;

const Blockers = styled.div`
  background: #fdf3e3;
  border-radius: 9px;
  color: #8a5a00;
  font-size: 0.82rem;
  padding: 0.55rem 0.7rem;
`;

const BlockerTitle = styled.div`
  align-items: center;
  display: flex;
  font-weight: 800;
  gap: 0.3rem;
  margin-bottom: 0.25rem;
`;

const Label = styled.label`
  color: #4a4f63;
  display: block;
  font-size: 0.78rem;
  font-weight: 800;
  margin: 0.6rem 0 0.25rem;
`;

const Input = styled.input`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.86rem;
  padding: 0.45rem 0.6rem;
  width: 100%;
`;

const Ack = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: ${theme.ink};
  cursor: pointer;
  display: flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 0.4rem;
  margin-top: 0.7rem;
  padding: 0;
  text-align: left;
`;

const Box = styled.span`
  align-items: center;
  background: ${(props) => (props.$on ? "#b42318" : "#ffffff")};
  border: 1px solid ${(props) => (props.$on ? "#b42318" : theme.border)};
  border-radius: 5px;
  color: #ffffff;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.7rem;
  height: 18px;
  justify-content: center;
  width: 18px;
`;

// The availability outcome is a deliberate, consequential choice, so it is rendered as two labelled
// options with their consequence spelled out — not a checkbox whose unticked state quietly means
// "sell the room again".
// Disabled rather than hidden when the choice does not apply: the front office knows this question is
// normally here, and a silently missing step reads as a bug. Showing it greyed with the reason states
// the rule at the moment it would otherwise be asked.
const Choice = styled.button`
  align-items: flex-start;
  background: ${(props) =>
    props.disabled ? "#f4f5f7" : props.$on ? "#fff8ef" : "#ffffff"};
  border: 1px solid ${(props) => (props.$on ? theme.gold : theme.border)};
  border-radius: 9px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  gap: 0.5rem;
  margin-top: 0.4rem;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  padding: 0.55rem 0.65rem;
  text-align: left;
  width: 100%;
`;

const ChoiceNote = styled.div`
  background: #eef4ff;
  border: 1px solid #c7d7fb;
  border-radius: 8px;
  color: #1e3a8a;
  font-size: 0.78rem;
  line-height: 1.45;
  margin-top: 0.4rem;
  padding: 0.45rem 0.6rem;
`;

const Radio = styled.span`
  background: ${(props) => (props.$on ? theme.gold : "#ffffff")};
  border: 1px solid ${(props) => (props.$on ? theme.gold : theme.border)};
  border-radius: 50%;
  box-shadow: ${(props) => (props.$on ? "inset 0 0 0 3px #ffffff" : "none")};
  flex: 0 0 auto;
  height: 16px;
  margin-top: 2px;
  width: 16px;
`;

const ChoiceTitle = styled.div`
  color: ${theme.ink};
  font-size: 0.84rem;
  font-weight: 800;
`;

const ChoiceHint = styled.div`
  color: #4a4f63;
  font-size: 0.78rem;
  line-height: 1.4;
  margin-top: 0.15rem;
`;

const Err = styled.div`
  background: #fdecea;
  border-radius: 8px;
  color: #b42318;
  font-size: 0.8rem;
  font-weight: 700;
  margin-top: 0.6rem;
  padding: 0.45rem 0.6rem;
`;

const Foot = styled.div`
  border-top: 1px solid ${theme.border};
  display: flex;
  gap: 0.45rem;
  justify-content: flex-end;
  padding: 0.7rem 1.1rem;
`;

const Btn = styled.button`
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.86rem;
  font-weight: 800;
  padding: 0.5rem 0.9rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Ghost = styled(Btn)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const Danger = styled(Btn)`
  background: #b42318;
  border: 0;
  color: #ffffff;
`;

const CancelBookingDialog = (props) => {
  const { t } = useT();
  const [check, setCheck] = useState(null);
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [availabilityAction, setAvailabilityAction] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    apiFetch(
      `/frontdesk_v2/bookings/${props.bookingId}/cancel_check?line_id=${props.lineId}`,
    )
      .then((res) => {
        if (active) setCheck(res?.data || { eligible: false, blockers: [] });
      })
      .catch(() => {
        if (active) setCheck({ eligible: false, blockers: [] });
      });
    return () => {
      active = false;
    };
  }, [props.bookingId, props.lineId]);

  const eligible = check?.eligible;
  // Server-computed. A transit-only booking holds no eZee inventory and writes no availability rows,
  // so neither branch has anything to act on and the server no longer asks for one.
  const availabilityApplies = check?.availability_choice_applies !== false;

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch(
        `/frontdesk_v2/bookings/${props.bookingId}/cancel`,
        {
          method: "POST",
          body: JSON.stringify({
            cancel_reason: reason.trim(),
            line_id: props.lineId,
            availability_action: availabilityApplies ? availabilityAction : "",
          }),
        },
      );
      if (res?.data?.booking) {
        props.onCanceled(res.data, res.message);
      } else {
        setError(res?.message || t("error_body"));
      }
    } catch {
      setError(t("error_body"));
    }
    setBusy(false);
  };

  return (
    <Backdrop onClick={props.onClose}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            <Ban size={16} />
            {t("cancel_title")}
          </Title>
          <Close type="button" aria-label={t("close")} onClick={props.onClose}>
            <X size={18} />
          </Close>
        </Head>

        <Body>
          <Intro>{t("cancel_intro")}</Intro>
          <Hard>{t("cancel_irreversible")}</Hard>

          {check && !eligible ? (
            <Blockers>
              <BlockerTitle>
                <AlertTriangle size={13} />
                {t("cancel_blocked_title")}
              </BlockerTitle>
              {(check.blockers || []).map((blocker) => (
                <div key={blocker}>· {blocker}</div>
              ))}
              <div>{t("cancel_blocked_hint")}</div>
            </Blockers>
          ) : null}

          {eligible ? (
            <>
              <Label htmlFor="cancel-reason">{t("cancel_reason_label")}</Label>
              <Input
                id="cancel-reason"
                value={reason}
                placeholder={t("cancel_reason_ph")}
                onChange={(event) => setReason(event.target.value)}
              />
              <Label as="div">{t("cancel_availability_label")}</Label>
              <Choice
                type="button"
                disabled={!availabilityApplies}
                aria-pressed={availabilityAction === "release"}
                $on={availabilityApplies && availabilityAction === "release"}
                onClick={() => setAvailabilityAction("release")}
              >
                <Radio
                  $on={availabilityApplies && availabilityAction === "release"}
                />
                <span>
                  <ChoiceTitle>{t("cancel_availability_release")}</ChoiceTitle>
                  <ChoiceHint>
                    {t("cancel_availability_release_hint")}
                  </ChoiceHint>
                </span>
              </Choice>
              <Choice
                type="button"
                disabled={!availabilityApplies}
                aria-pressed={availabilityAction === "keep_blocked"}
                $on={
                  availabilityApplies && availabilityAction === "keep_blocked"
                }
                onClick={() => setAvailabilityAction("keep_blocked")}
              >
                <Radio
                  $on={
                    availabilityApplies && availabilityAction === "keep_blocked"
                  }
                />
                <span>
                  <ChoiceTitle>{t("cancel_availability_keep")}</ChoiceTitle>
                  <ChoiceHint>{t("cancel_availability_keep_hint")}</ChoiceHint>
                </span>
              </Choice>
              {availabilityApplies ? null : (
                <ChoiceNote>{t("cancel_availability_transit_note")}</ChoiceNote>
              )}

              <Ack
                type="button"
                aria-pressed={ack}
                onClick={() => setAck(!ack)}
              >
                <Box $on={ack}>{ack ? "✓" : null}</Box>
                {t("cancel_ack")}
              </Ack>
            </>
          ) : null}

          {error ? <Err>{error}</Err> : null}
        </Body>

        <Foot>
          <Ghost type="button" onClick={props.onClose}>
            {t("confirm_cancel")}
          </Ghost>
          <Danger
            type="button"
            disabled={
              !eligible ||
              !ack ||
              !reason.trim() ||
              (availabilityApplies && !availabilityAction) ||
              busy
            }
            onClick={submit}
          >
            {t("cancel_confirm")}
          </Danger>
        </Foot>
      </Card>
    </Backdrop>
  );
};

export default CancelBookingDialog;
